/**
 * PaymentApplicationsPage — Issue #370.
 *
 * 付款申请单列表 + 创建表单 + 审批操作。所有 API 调用通过 @jinzhuan/api-client
 * 进入真实后端；前端不维护任何审批状态。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  usePaymentApplicationActions,
  usePaymentApplications,
  type CreatePaymentApplicationDto,
  type PaymentApplicationSummaryDto,
  type PaymentMethod,
} from '@/hooks/useFinanceSettlement';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  pending_department_approval: '待部门审核',
  pending_finance_review: '待财务复核',
  pending_extra_approval: '待额外审批',
  pending_cashier_execution: '待出纳付款',
  executed: '已付款',
  rejected: '已驳回',
  cancelled: '已撤回',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_department_approval: 'bg-sky-100 text-sky-700',
  pending_finance_review: 'bg-blue-100 text-blue-700',
  pending_extra_approval: 'bg-amber-100 text-amber-800',
  pending_cashier_execution: 'bg-purple-100 text-purple-800',
  executed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-500',
};

const PAYMENT_METHODS: ReadonlyArray<{ value: PaymentMethod; label: string }> = [
  { value: 'bank_transfer', label: '银行转账' },
  { value: 'cash', label: '现金' },
  { value: 'check', label: '支票' },
  { value: 'wechat', label: '微信' },
  { value: 'alipay', label: '支付宝' },
  { value: 'other', label: '其他' },
];

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function emptyDraft(): CreatePaymentApplicationDto {
  return {
    applicationNo: '',
    applicationDate: new Date().toISOString().slice(0, 10),
    departmentName: '',
    applicantName: '',
    payeeAccountId: '',
    applicationAmount: '',
    paymentMethod: 'bank_transfer',
  };
}

export default function PaymentApplicationsPage() {
  const { permissions } = useAuth();
  const granted = permissions ?? [];
  const canWrite = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYMENT_APPLICATION_WRITE,
  );
  const canDepartmentApprove = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYMENT_APPLICATION_DEPARTMENT_APPROVE,
  );
  const canFinanceReview = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYMENT_APPLICATION_FINANCE_REVIEW,
  );
  const canExtraApprove = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYMENT_APPLICATION_EXTRA_APPROVE,
  );
  const canCashier = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYMENT_APPLICATION_CASHIER_EXECUTE,
  );
  const canManage = hasPermission(
    granted,
    FINANCE_PERMISSIONS.PAYMENT_APPLICATION_MANAGE,
  );

  const { data, loading, error, fetchList } = usePaymentApplications();
  const actions = usePaymentApplicationActions();

  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [draft, setDraft] = useState<CreatePaymentApplicationDto>(emptyDraft());
  const [creating, setCreating] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  useEffect(() => {
    void fetchList({ status: statusFilter || undefined, search: searchTerm || undefined });
  }, [fetchList, statusFilter, searchTerm]);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const submitDraft = async () => {
    if (!canWrite) return;
    setActionMessage(null);
    const result = await actions.create(draft);
    if (result) {
      setActionMessage(`已创建付款申请：${result.applicationNo}`);
      setDraft(emptyDraft());
      setCreating(false);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleSubmit = async (row: PaymentApplicationSummaryDto) => {
    setActionMessage(null);
    const result = await actions.submit(row.id, row.recordVersion);
    if (result) {
      setActionMessage(`${result.applicationNo} 已提交`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleDepartmentReview = async (row: PaymentApplicationSummaryDto) => {
    setActionMessage(null);
    const result = await actions.departmentReview(row.id, {
      expectedRecordVersion: row.recordVersion,
    });
    if (result) {
      setActionMessage(
        `${result.applicationNo} 部门审核通过 -> ${STATUS_LABELS[result.status]}`,
      );
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleFinanceReview = async (row: PaymentApplicationSummaryDto) => {
    setActionMessage(null);
    const result = await actions.financeReview(row.id, {
      expectedRecordVersion: row.recordVersion,
    });
    if (result) {
      setActionMessage(`${result.applicationNo} 财务复核通过 -> ${STATUS_LABELS[result.status]}`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleExtraApprove = async (row: PaymentApplicationSummaryDto) => {
    setActionMessage(null);
    const result = await actions.extraApprove(row.id, {
      expectedRecordVersion: row.recordVersion,
    });
    if (result) {
      setActionMessage(`${result.applicationNo} 额外审批通过`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleCashier = async (row: PaymentApplicationSummaryDto) => {
    setActionMessage(null);
    const result = await actions.cashierExecute(row.id, {
      expectedRecordVersion: row.recordVersion,
      paymentMethod: row.paymentMethod,
      executedAt: new Date().toISOString().slice(0, 10),
    });
    if (result) {
      setActionMessage(`${result.applicationNo} 出纳已执行`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const promptReason = (prefix: string): string | null => {
    const reason = window.prompt(`${prefix}原因（必填）`);
    if (reason === null) return null;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setActionMessage(null);
      return null;
    }
    return trimmed;
  };

  const handleDepartmentReject = async (row: PaymentApplicationSummaryDto) => {
    const reason = promptReason('部门审核驳回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.departmentReject(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.applicationNo} 已被部门驳回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleFinanceReject = async (row: PaymentApplicationSummaryDto) => {
    const reason = promptReason('财务复核驳回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.financeReject(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.applicationNo} 已被财务驳回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleCancel = async (row: PaymentApplicationSummaryDto) => {
    const reason = promptReason('撤回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.cancel(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.applicationNo} 已撤回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleReturnToDraft = async (row: PaymentApplicationSummaryDto) => {
    setActionMessage(null);
    const result = await actions.returnToDraft(row.id, row.recordVersion);
    if (result) {
      setActionMessage(`${result.applicationNo} 已回到草稿`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const canCancel = (row: PaymentApplicationSummaryDto): boolean => {
    if (!canManage) return false;
    return (
      row.status === 'draft' ||
      row.status === 'pending_department_approval' ||
      row.status === 'pending_finance_review' ||
      row.status === 'pending_extra_approval' ||
      row.status === 'pending_cashier_execution'
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">付款申请单</h1>
        {canWrite ? (
          <button
            onClick={() => setCreating((v) => !v)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground shadow-sm"
          >
            {creating ? '取消' : '新建'}
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          className="rounded border px-2 py-1 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="rounded border px-2 py-1 text-sm"
          placeholder="搜索单号 / 部门 / 申请人"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {actionMessage ? (
        <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {actionMessage}
        </div>
      ) : null}
      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}
      {actions.error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {actions.error.message}
        </div>
      ) : null}

      {creating ? (
        <div className="space-y-3 rounded border bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold">新建付款申请</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">申请单号</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.applicationNo}
                onChange={(e) => setDraft({ ...draft, applicationNo: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">申请日期</span>
              <input
                type="date"
                className="rounded border px-2 py-1"
                value={draft.applicationDate}
                onChange={(e) => setDraft({ ...draft, applicationDate: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">部门</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.departmentName}
                onChange={(e) => setDraft({ ...draft, departmentName: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">申请人</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.applicantName}
                onChange={(e) => setDraft({ ...draft, applicantName: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">收款账户 ID（来自 #369 主数据）</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.payeeAccountId}
                onChange={(e) => setDraft({ ...draft, payeeAccountId: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">申请金额</span>
              <input
                type="text"
                inputMode="decimal"
                className="rounded border px-2 py-1"
                value={draft.applicationAmount}
                onChange={(e) => setDraft({ ...draft, applicationAmount: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">付款方式</span>
              <select
                className="rounded border px-2 py-1"
                value={draft.paymentMethod}
                onChange={(e) =>
                  setDraft({ ...draft, paymentMethod: e.target.value as PaymentMethod })
                }
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">项目编号</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.projectCode ?? ''}
                onChange={(e) => setDraft({ ...draft, projectCode: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">合同编号</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.contractCode ?? ''}
                onChange={(e) => setDraft({ ...draft, contractCode: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              <span className="text-gray-600">临时覆盖银行账号（明文，仅在确需变更时填写，将触发额外审批）</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.overrideBankAccountPlaintext ?? ''}
                onChange={(e) => setDraft({ ...draft, overrideBankAccountPlaintext: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              <span className="text-gray-600">备注</span>
              <textarea
                className="rounded border px-2 py-1"
                rows={2}
                value={draft.remarks ?? ''}
                onChange={(e) => setDraft({ ...draft, remarks: e.target.value || undefined })}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setCreating(false);
                setDraft(emptyDraft());
              }}
              className="rounded border px-3 py-1.5 text-sm"
            >
              取消
            </button>
            <button
              onClick={submitDraft}
              disabled={actions.loading}
              className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground"
            >
              {actions.loading ? '提交中…' : '保存草稿'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">单号</th>
              <th className="px-3 py-2 text-left">申请日期</th>
              <th className="px-3 py-2 text-left">部门 / 申请人</th>
              <th className="px-3 py-2 text-right">申请金额</th>
              <th className="px-3 py-2 text-left">付款方式</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                  加载中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                  暂无数据
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{row.applicationNo}</div>
                    {row.extraApprovalRequired ? (
                      <div className="text-xs text-amber-600">需额外审批</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{row.applicationDate}</td>
                  <td className="px-3 py-2">
                    <div>{row.departmentName}</div>
                    <div className="text-xs text-gray-500">{row.applicantName}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {row.applicationAmount}
                  </td>
                  <td className="px-3 py-2">{row.paymentMethod}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                  <td
                    className="space-x-1 px-3 py-2"
                    data-testid={`payment-application-row-actions-${row.id}`}
                  >
                    {row.status === 'draft' && canWrite ? (
                      <button
                        data-testid={`payment-application-action-submit-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleSubmit(row)}
                      >
                        提交
                      </button>
                    ) : null}
                    {row.status === 'pending_department_approval' && canDepartmentApprove ? (
                      <button
                        data-testid={`payment-application-action-department-review-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleDepartmentReview(row)}
                      >
                        部门审核
                      </button>
                    ) : null}
                    {row.status === 'pending_department_approval' && canDepartmentApprove ? (
                      <button
                        data-testid={`payment-application-action-department-reject-${row.id}`}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700"
                        onClick={() => handleDepartmentReject(row)}
                      >
                        部门驳回
                      </button>
                    ) : null}
                    {row.status === 'pending_finance_review' && canFinanceReview ? (
                      <button
                        data-testid={`payment-application-action-finance-review-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleFinanceReview(row)}
                      >
                        财务复核
                      </button>
                    ) : null}
                    {(row.status === 'pending_finance_review' ||
                      row.status === 'pending_extra_approval' ||
                      row.status === 'pending_cashier_execution') &&
                    canFinanceReview ? (
                      <button
                        data-testid={`payment-application-action-finance-reject-${row.id}`}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700"
                        onClick={() => handleFinanceReject(row)}
                      >
                        财务驳回
                      </button>
                    ) : null}
                    {row.status === 'pending_extra_approval' && canExtraApprove ? (
                      <button
                        data-testid={`payment-application-action-extra-approve-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleExtraApprove(row)}
                      >
                        额外审批
                      </button>
                    ) : null}
                    {row.status === 'pending_cashier_execution' && canCashier ? (
                      <button
                        data-testid={`payment-application-action-cashier-execute-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleCashier(row)}
                      >
                        出纳执行
                      </button>
                    ) : null}
                    {canCancel(row) ? (
                      <button
                        data-testid={`payment-application-action-cancel-${row.id}`}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                        onClick={() => handleCancel(row)}
                      >
                        撤回
                      </button>
                    ) : null}
                    {row.status === 'rejected' && canWrite ? (
                      <button
                        data-testid={`payment-application-action-return-to-draft-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleReturnToDraft(row)}
                      >
                        回到草稿
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
